import { WidgetConfig, RecommendProduct } from "common/lib/interfaces";

import { Theme } from "common/lib/constants/config";
import { CONTEXTUAL_RECOMMENDS_WIDGET_DATA_ATTRIBUTE } from "../constants/components.registry";

declare const BASE_PUBLIC_URL: string;
declare const BASE_API_URL: string;
declare const ENTRY_POINT: string;

export const getScriptQueryParams = (): string => {
    const script: HTMLScriptElement | null = document.querySelector(
        `script[src*="${ENTRY_POINT}.js"]`
    );

    const src = script?.src ?? "";

    const index = src.indexOf("?");

    return index !== -1 ? src.substring(index) : "";
};

export const loadCSS = async (
    url: string,
    id: string,
    element: HTMLElement = document.head
) => {
    const params = getScriptQueryParams();

    return !document.getElementById(id)
        ? new Promise((resolve, reject) => {
              const linkEl = document.createElement("link");
              linkEl.href = `${url}${params}`;
              linkEl.rel = "stylesheet";
              linkEl.type = "text/css";
              linkEl.id = id;
              linkEl.onload = () => resolve(1);
              linkEl.onerror = err => reject(err);
              element.appendChild(linkEl);
          })
        : Promise.resolve();
};

export const loadCTThemeCSS = async (
    theme: Theme,
    networkBrand = "nbcu-trial"
) =>
    loadCSS(
        `${BASE_API_URL}/v4/${networkBrand}/theme/${theme}`,
        "usc-theme-styles"
    )
        .then(() => console.log(`CT: ${theme} theme loaded`))
        .catch(err => console.log(`CT: ${theme} theme not loaded`, err));

export const loadWidgetV3 = async () =>
    Promise.all([
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        // eslint-disable-next-line import/extensions
        import("widgetV3/usc-v3.js"),
        loadCSS(`${BASE_PUBLIC_URL}/widget-v3.css`, "usc-styles")
    ]).catch(err => {
        console.log("USC: can not load widgetV3' css/js", err);
    });

export const loadAmazonPayScript = (): void => {
    const script = document.createElement("script");
    script.src = "https://static-na.payments-amazon.com/checkout.js";
    document.body.appendChild(script);
};

export const loadContextualRecommendationsWidget = async (
    config: WidgetConfig,
    recommendProducts: RecommendProduct[],
    productsCount: number,
    areSponsoredCardsAllowed: boolean
): Promise<void> => {
    const rootElements = document.querySelectorAll(
        `[${CONTEXTUAL_RECOMMENDS_WIDGET_DATA_ATTRIBUTE}]`
    );
    if (!rootElements.length) {
        return;
    }

    Promise.all([
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        // eslint-disable-next-line import/no-unresolved
        import("contextualWidget/uscContextualWidget.js"),
        loadCSS(
            `${BASE_PUBLIC_URL}/uscContextualWidget.css`,
            "usc-contextual-styles"
        )
    ])
        .then(() => {
            if (window.uscContextualWidget) {
                const recommendationConfig = {
                    ...config,
                    recommendProducts,
                    productsCount,
                    areSponsoredCardsAllowed
                };

                window.uscContextualWidget.showRecommendations(
                    recommendationConfig,
                    rootElements
                );
            }
        })
        .catch(err => {
            console.log(
                "USC: can not load contextual recommends widget' css/js",
                err
            );
        });
};

export const loadEmbeddableProductsWidget = async () => {
    return Promise.all([
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        // eslint-disable-next-line import/no-unresolved
        import("embeddableWidget/uscEmbeddableProductsWidget.js"),
        loadCSS(
            `${BASE_PUBLIC_URL}/uscEmbeddableProductsWidget.css`,
            "usc-embeddable-products-styles"
        )
    ]);
};

export const loadPromisePolyfill = () => {
    if (!("onunhandledrejection" in window)) {
        try {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            import("../promise-polyfill");
        } catch (e) {
            console.log("USC: can't load promise polyfill", e);
        }
    }
};

type TLoadDefaultConfig = (
    networkBrand?: string,
    theme?: string
) => Promise<WidgetConfig>;
export const loadDefaultConfig: TLoadDefaultConfig = async (
    networkBrand = "nbcu-trial",
    theme = "some-name"
) => {
    try {
        const params = getScriptQueryParams();

        const response = await fetch(
            `${BASE_API_URL}/v4/${networkBrand}/theme/${theme}/config${params}`
        );
        const defaultConfig = await response.json();
        return defaultConfig || {};
    } catch (error) {
        console.error("USC: fetch config failed.", error);
        return {};
    }
};
